import { useState, useMemo, useCallback, useEffect } from 'react';
import { TranscriptUtterance } from '../types';

export interface LowConfidenceWord {
  utteranceId: string;
  wordId: string;
}

export interface LowConfidenceNavigationState {
  lowConfidenceWords: LowConfidenceWord[];
  currentIndex: number;
  isActive: boolean;
  goToNext: () => void;
  goToPrev: () => void;
}

export const useLowConfidenceNavigation = (
  utterances: TranscriptUtterance[],
  onNavigateToWord: (utteranceId: string, wordId: string) => void,
  audioFileId?: string
): LowConfidenceNavigationState => {
  const [currentIndex, setCurrentIndex] = useState(-1);

  // Reset state when audio file changes
  useEffect(() => {
    setCurrentIndex(-1);
  }, [audioFileId]);

  // Find all low-confidence words in the transcript
  const lowConfidenceWords = useMemo(() => {
    const words: LowConfidenceWord[] = [];

    utterances.forEach((utterance) => {
      utterance.transcriptWords.forEach((word) => {
        if (word.confidenceScore < 0.8) {
          words.push({
            utteranceId: utterance.id,
            wordId: word.id,
          });
        }
      });
    });

    return words;
  }, [utterances]);

  // Reset current index if words change and current index is out of bounds
  useMemo(() => {
    if (lowConfidenceWords.length === 0) {
      setCurrentIndex(-1);
    } else if (currentIndex >= lowConfidenceWords.length) {
      setCurrentIndex(-1);
    }
  }, [lowConfidenceWords, currentIndex]);

  // Navigation functions
  const goToNext = useCallback(() => {
    if (lowConfidenceWords.length === 0) return;

    // If not yet activated (currentIndex === -1), start at first word
    const nextIndex =
      currentIndex === -1
        ? 0
        : currentIndex >= lowConfidenceWords.length - 1
          ? 0
          : currentIndex + 1;

    // Set found word as current word and scroll
    setCurrentIndex(nextIndex);
    const word = lowConfidenceWords[nextIndex];
    onNavigateToWord(word.utteranceId, word.wordId);
  }, [lowConfidenceWords, currentIndex, onNavigateToWord]);

  const goToPrev = useCallback(() => {
    if (lowConfidenceWords.length === 0) return;

    // If not yet activated (currentIndex === -1), start at last word
    const prevIndex =
      currentIndex === -1
        ? lowConfidenceWords.length - 1
        : currentIndex <= 0
          ? lowConfidenceWords.length - 1
          : currentIndex - 1;

    // Set found word as current word and scroll
    setCurrentIndex(prevIndex);
    const word = lowConfidenceWords[prevIndex];
    onNavigateToWord(word.utteranceId, word.wordId);
  }, [lowConfidenceWords, currentIndex, onNavigateToWord]);

  return {
    lowConfidenceWords,
    currentIndex,
    isActive: currentIndex >= 0,
    goToNext,
    goToPrev,
  };
};
